// src/components/RootErrorBoundary.js
//
// Uygulamanın en dışındaki hata sınırı.
//
// React 18'de kök ağaçta yakalanmamış bir çizim hatası olursa React BÜTÜN
// ağacı söker ve kullanıcı bomboş beyaz bir sayfayla baş başa kalır; ne olduğunu
// anlamanın, geri dönmenin ya da yenilemenin bir yolu yoktur. Mobil Chrome'da
// bu beyaz ekran çoğu zaman sekmenin tamamen çökmesiyle ("Bu sayfa açılamıyor")
// sonuçlanıyordu.
//
// Bu bileşen o boşluğu doldurur: hatayı yakalar, kısa bir açıklama ve "Sayfayı
// yenile" düğmesi gösterir. Kasıtlı olarak uygulama stillerine, yönlendiriciye
// veya başka bir bileşene bağımlı değildir; hangi parça bozulmuş olursa olsun
// çizilebilmelidir.

import React from "react";

// Yeni sürüm yayınlandıktan sonra açık kalan sekme, eski sürümün artık
// sunucuda olmayan sayfa parçalarını (chunk) ister ve yüklenemez. Bu bir kod
// hatası değildir; sayfayı bir kez yenilemek yeni sürümü getirir. Bu durumda
// kullanıcıya hata ekranı göstermek yerine sayfa kendiliğinden yenilenir.
// Sonsuz döngüye girmemek için 30 saniye içinde yalnızca bir kez denenir.
const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unexpected token '<'/i;
const RELOAD_KEY = "elitlig:chunk-reload-at";
const RELOAD_WINDOW_MS = 30000;

export const isChunkLoadError = (error) =>
  Boolean(error) &&
  (error.name === "ChunkLoadError" ||
    CHUNK_ERROR_PATTERN.test(String(error.message || error)));

// Hata metnindeki yüklenemeyen JS/CSS adresleri.
export const failedAssetUrls = (error) =>
  Array.from(
    new Set(String(error?.message || error || "").match(/https?:\/\/[^\s)"']+\.(?:js|css)\b/g) || [])
  );

// Tarayıcı bozuk/eksik bir parçanın yanıtını önbellekte tutmuş olabilir;
// sayfayı yenilemek o kaydı kullanmaya devam eder. Bu adresleri önbelleği
// atlayarak yeniden ister (cache: "reload" önbellek kaydını da tazeler),
// sonra sayfayı yeniler. İstek başarısız olsa ya da 4 sn'de bitmese de
// yenileme yapılır.
export const refreshAssetsAndReload = (urls = []) => {
  const fetches = typeof window.fetch === "function"
    ? urls.map((url) => window.fetch(url, { cache: "reload", credentials: "same-origin" }).catch(() => null))
    : [];
  const timeout = new Promise((resolve) => setTimeout(resolve, 4000));
  return Promise.race([Promise.all(fetches), timeout]).finally(() => window.location.reload());
};

// Yakın zamanda denenmediyse önbelleği tazeleyip sayfayı yeniler; yenilediyse true döner.
export const reloadOnceForChunkError = (error) => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Depolama kapalıysa (gizli mod vb.) döngü riskine girmeden vazgeç.
    return false;
  }
  refreshAssetsAndReload(failedAssetUrls(error));
  return true;
};

const wrapStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  background: "#0f172a",
  color: "#f8fafc",
  fontFamily:
    "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  textAlign: "center",
  boxSizing: "border-box",
};

const buttonStyle = {
  marginTop: "20px",
  padding: "12px 24px",
  border: "none",
  borderRadius: "999px",
  background: "#22c55e",
  color: "#052e16",
  fontSize: "15px",
  fontWeight: 700,
  cursor: "pointer",
};

export default class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reloading: false, message: "", assets: [] };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: String(error?.message || error || "").slice(0, 300), assets: failedAssetUrls(error) };
  }

  componentDidCatch(error, info) {
    // Geliştirme sırasında konsolda görünsün; üretimde de en azından iz kalsın.
    // eslint-disable-next-line no-console
    console.error("Uygulama çizim hatası:", error, info?.componentStack);
    if (isChunkLoadError(error) && reloadOnceForChunkError(error)) {
      this.setState({ reloading: true });
    }
  }

  // Elle yenilemede de yüklenemeyen parçalar önbellek atlanarak tazelenir.
  handleReload = () => {
    if (this.state.assets.length) {
      this.setState({ reloading: true });
      refreshAssetsAndReload(this.state.assets);
      return;
    }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.reloading) {
      return (
        <div role="status" style={wrapStyle}>
          <p style={{ margin: 0, opacity: 0.85 }}>Sitenin yeni sürümü yükleniyor…</p>
        </div>
      );
    }

    return (
      <div role="alert" style={wrapStyle}>
        <div style={{ maxWidth: "420px" }}>
          <h1 style={{ fontSize: "22px", margin: "0 0 8px" }}>
            Bir sorun oluştu
          </h1>
          <p style={{ margin: 0, opacity: 0.85, lineHeight: 1.5 }}>
            Sayfa beklenmedik bir hatayla karşılaştı. Yenilemek genellikle
            sorunu çözer.
          </p>
          <button type="button" style={buttonStyle} onClick={this.handleReload}>
            Sayfayı yenile
          </button>
          {/* Sorun tekrarlarsa bildirilebilsin diye hatanın kısa metni. */}
          {this.state.message && (
            <details style={{ marginTop: "18px", fontSize: "12px", opacity: 0.6 }}>
              <summary style={{ cursor: "pointer" }}>Hata ayrıntısı</summary>
              <code style={{ display: "block", marginTop: "6px", wordBreak: "break-word" }}>{this.state.message}</code>
            </details>
          )}
        </div>
      </div>
    );
  }
}
